/**
 * Remédios: o esquema de pé e a dose da vez.
 *
 * A tela responde à pergunta que a mãe faz com a criança no colo — *já dei a
 * das 15h?* — e por isso o dia inteiro do remédio aparece de uma vez: as horas
 * de hoje, qual já foi, qual é a próxima. Um botão só, o da próxima.
 *
 * A barra de dose é a única contagem do produto (regra 2 do CLAUDE.md). Ela
 * existe em tratamento com prazo, onde saber que faltam doze doses é cuidado,
 * e não existe em uso contínuo, onde uma barra que nunca enche viraria régua.
 *
 * O que é editável aqui é o que a **Ruth** entendeu, não o que o médico
 * escreveu. Ela transcreve de ouvido ou de foto, e transcrição erra: um
 * "de 8 em 8" ouvido como "de 6 em 6" vira criança tomando remédio na hora
 * errada. Até aqui o único conserto era apagar o esquema e ditar tudo de novo,
 * o que ninguém faz com criança no colo. Receita nova continua sendo foto
 * nova — corrigir não é receitar, e a tela diz isso com todas as letras.
 *
 * Medidas de docs/design/Remedios.dc.html.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react'

import {
  api,
  fraseDoErro,
  fotoDaReceita,
  hhmm,
  type CorrecaoDeRemedio,
  type RemedioNovo,
  type DoseDeHoje,
  type Receita,
  type Remedio,
} from '../api'
import { Icone } from '../componentes/Icone'
import '../estilos/remedios.css'
import '../estilos/conserto.css'

type Props = {
  /** Ler receita é conversa com a Ruth, não formulário: quem abre a câmera é de fora. */
  aoFotografarReceita: () => void
}

export function Remedios({ aoFotografarReceita }: Props) {
  const [remedios, setRemedios] = useState<Remedio[] | null>(null)
  const [marcando, setMarcando] = useState<string | null>(null)
  const [corrigindo, setCorrigindo] = useState<string | null>(null)
  const [montando, setMontando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    try {
      setRemedios((await api.remedios()).remedios)
      setErro(null)
    } catch (e) {
      setErro(fraseDoErro(e, 'não consegui abrir seus remédios'))
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  /**
   * Marcar uma dose muda a barra, as bolinhas do dia e qual é a próxima. Em vez
   * de adivinhar as três coisas, a tela recarrega e mostra o que o servidor diz.
   */
  const dei = useCallback(
    async (dose: string) => {
      setMarcando(dose)
      setErro(null)
      try {
        await api.dei(dose)
        await carregar()
      } catch (e) {
        setErro(fraseDoErro(e, 'não consegui marcar essa dose agora'))
      } finally {
        setMarcando(null)
      }
    },
    [carregar],
  )

  /**
   * Salvar uma correção pode refazer o esquema inteiro: outras horas, outra
   * contagem, outra próxima dose. A tela não tenta adivinhar o resultado —
   * pergunta ao servidor de novo, como no "dei a dose".
   */
  const salvar = useCallback(
    async (remedio: string, campos: CorrecaoDeRemedio) => {
      await api.corrigirRemedio(remedio, campos)
      setCorrigindo(null)
      await carregar()
    },
    [carregar],
  )

  const montar = useCallback(
    async (novo: RemedioNovo) => {
      await api.montarRemedio(novo)
      setMontando(false)
      await carregar()
    },
    [carregar],
  )

  const encerrar = useCallback(
    async (remedio: string) => {
      await api.encerrarRemedio(remedio)
      setCorrigindo(null)
      await carregar()
    },
    [carregar],
  )

  const emCurso = (remedios ?? []).filter((r) => !r.continuo)
  const todoDia = (remedios ?? []).filter((r) => r.continuo)

  return (
    <>
      <header className="cabeca">
        <div className="cabeca__datas">
          <h1 className="cabeca__titulo">Remédios</h1>
        </div>
      </header>

      <div className="corpo">
        {erro && <p className="vazio">{erro}</p>}

        {remedios && remedios.length === 0 && !erro && (
          <p className="vazio">Nada de remédio agora. Se aparecer receita, me manda a foto.</p>
        )}

        {emCurso.length > 0 && <div className="eyebrow">Em andamento</div>}
        {emCurso.map((remedio) => (
          <Tratamento
            key={remedio.id}
            remedio={remedio}
            marcando={marcando}
            corrigindo={corrigindo === remedio.id}
            aoMarcar={(dose) => void dei(dose)}
            aoCorrigir={() => setCorrigindo(remedio.id)}
            aoFechar={() => setCorrigindo(null)}
            aoSalvar={(campos) => salvar(remedio.id, campos)}
            aoEncerrar={() => encerrar(remedio.id)}
          />
        ))}

        {todoDia.length > 0 && <div className="eyebrow">Todo dia</div>}
        {todoDia.map((remedio) => (
          <TodoDia
            key={remedio.id}
            remedio={remedio}
            marcando={marcando}
            corrigindo={corrigindo === remedio.id}
            aoMarcar={(dose) => void dei(dose)}
            aoCorrigir={() => setCorrigindo(remedio.id)}
            aoFechar={() => setCorrigindo(null)}
            aoSalvar={(campos) => salvar(remedio.id, campos)}
            aoEncerrar={() => encerrar(remedio.id)}
          />
        ))}

        <button type="button" className="convite" onClick={aoFotografarReceita}>
          <Icone nome="camera" tamanho={20} />
          Fotografar uma receita
        </button>

        {montando ? (
          <Montar aoMontar={montar} aoFechar={() => setMontando(false)} />
        ) : (
          <button type="button" className="convite" onClick={() => setMontando(true)}>
            <Icone nome="somar" tamanho={20} />
            Escrever eu mesma
          </button>
        )}
      </div>
    </>
  )
}

type CartaoProps = {
  remedio: Remedio
  marcando: string | null
  corrigindo: boolean
  aoMarcar: (dose: string) => void
  aoCorrigir: () => void
  aoFechar: () => void
  aoSalvar: (campos: CorrecaoDeRemedio) => Promise<void>
  aoEncerrar: () => Promise<void>
}

/** Tratamento com prazo: barra de dose, o dia inteiro e o botão da próxima. */
function Tratamento({
  remedio,
  marcando,
  corrigindo,
  aoMarcar,
  aoCorrigir,
  aoFechar,
  aoSalvar,
  aoEncerrar,
}: CartaoProps) {
  const proxima = remedio.doses_de_hoje.find((d) => d.id === remedio.proxima_dose_id)

  return (
    <div className="cartao remedio remedio--curso">
      <div className="remedio__topo">
        <span className="remedio__icone remedio__icone--curso">
          <Icone nome="remedio" tamanho={20} />
        </span>
        <span className="remedio__texto">
          <span className="remedio__nome">
            {remedio.nome} · {remedio.para_quem}
          </span>
          <span className="remedio__meta">{descrever(remedio)}</span>
        </span>
      </div>

      {remedio.dose_atual !== null && remedio.total !== null && (
        <Barra atual={remedio.dose_atual} total={remedio.total} />
      )}

      {remedio.doses_de_hoje.length > 0 && (
        <div className="remedio__dia">
          <div className="remedio__doses">
            {remedio.doses_de_hoje.map((dose) => (
              <Bolinha key={dose.id} dose={dose} />
            ))}
          </div>

          {proxima && (
            <button
              type="button"
              className="botao-primario remedio__dei"
              disabled={marcando === proxima.id}
              onClick={() => aoMarcar(proxima.id)}
            >
              Dei a das {hhmm(proxima.hora)}
            </button>
          )}
        </div>
      )}

      {remedio.receita && <DaReceita receita={remedio.receita} />}

      {corrigindo ? (
        <Correcao
          remedio={remedio}
          aoSalvar={aoSalvar}
          aoFechar={aoFechar}
          aoEncerrar={aoEncerrar}
        />
      ) : (
        <BotaoCorrigir remedio={remedio} aoCorrigir={aoCorrigir} />
      )}
    </div>
  )
}

/** Uso contínuo: sem contagem, sem barra. A bolinha do lado é o dia de hoje. */
function TodoDia({
  remedio,
  marcando,
  corrigindo,
  aoMarcar,
  aoCorrigir,
  aoFechar,
  aoSalvar,
  aoEncerrar,
}: CartaoProps) {
  const proxima = remedio.doses_de_hoje.find((d) => d.id === remedio.proxima_dose_id)
  const tem = remedio.doses_de_hoje.length > 0

  return (
    <div className="cartao remedio">
      <div className="remedio__topo">
        <span className="remedio__icone">
          <Icone nome="gota" tamanho={20} />
        </span>
        <span className="remedio__texto">
          <span className="remedio__nome">
            {remedio.nome} · {remedio.para_quem}
          </span>
          <span className="remedio__meta">{descrever(remedio)}</span>
        </span>

        {proxima ? (
          <button
            type="button"
            className="remedio__marca"
            aria-label={`${remedio.nome}: dei a de ${hhmm(proxima.hora)}`}
            disabled={marcando === proxima.id}
            onClick={() => aoMarcar(proxima.id)}
          />
        ) : (
          tem && (
            <span
              className="remedio__marca remedio__marca--dada"
              aria-label={`${remedio.nome}: dose de hoje dada`}
            >
              <Icone nome="check" tamanho={14} />
            </span>
          )
        )}
      </div>

      {corrigindo ? (
        <Correcao
          remedio={remedio}
          aoSalvar={aoSalvar}
          aoFechar={aoFechar}
          aoEncerrar={aoEncerrar}
        />
      ) : (
        <BotaoCorrigir remedio={remedio} aoCorrigir={aoCorrigir} />
      )}
    </div>
  )
}

function BotaoCorrigir({ remedio, aoCorrigir }: { remedio: Remedio; aoCorrigir: () => void }) {
  return (
    <button
      type="button"
      className="remedio__corrigir"
      aria-label={`Corrigir o que entendi de ${remedio.nome}`}
      onClick={aoCorrigir}
    >
      Corrigir o que eu entendi
    </button>
  )
}

/**
 * O formulário de correção, já preenchido com o que a Ruth entendeu.
 *
 * Vem preenchido porque quase sempre ela quer mexer em uma coisa só, e um
 * formulário em branco a obrigaria a redigitar o que já estava certo — num
 * lugar onde redigitar errado tem consequência.
 *
 * Manda só o que ela mudou. Sem isso, abrir e salvar sem tocar em nada
 * reescreveria campos que estavam bons, e "salvei igual" não é igual: mexer no
 * intervalo refaz todas as doses que ainda não aconteceram.
 */
function Correcao({
  remedio,
  aoSalvar,
  aoFechar,
  aoEncerrar,
}: {
  remedio: Remedio
  aoSalvar: (campos: CorrecaoDeRemedio) => Promise<void>
  aoFechar: () => void
  aoEncerrar: () => Promise<void>
}) {
  const [encerrando, setEncerrando] = useState(false)
  const [nome, setNome] = useState(remedio.nome)
  const [concentracao, setConcentracao] = useState(remedio.concentracao ?? '')
  const [dose, setDose] = useState(remedio.dose)
  const [via, setVia] = useState(remedio.via ?? '')
  const [horas, setHoras] = useState(String(remedio.intervalo_horas))
  const [dias, setDias] = useState(remedio.duracao_dias ? String(remedio.duracao_dias) : '')
  const [proxima, setProxima] = useState(paraCampo(remedio.proxima_dose_em))
  const [observacoes, setObservacoes] = useState(remedio.observacoes ?? '')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const salvar = async (evento: FormEvent) => {
    evento.preventDefault()
    if (salvando) return

    const campos: CorrecaoDeRemedio = {}
    if (nome.trim() && nome.trim() !== remedio.nome) campos.nome = nome.trim()
    if (dose.trim() && dose.trim() !== remedio.dose) campos.dose = dose.trim()
    if ((concentracao.trim() || null) !== remedio.concentracao)
      campos.concentracao = concentracao.trim() || null
    if ((via.trim() || null) !== remedio.via) campos.via = via.trim() || null
    if ((observacoes.trim() || null) !== remedio.observacoes)
      campos.observacoes = observacoes.trim() || null

    const emHoras = Number(horas)
    if (emHoras >= 1 && emHoras !== remedio.intervalo_horas) campos.intervalo_horas = emHoras

    const emDias = dias.trim() ? Number(dias) : null
    if (emDias !== remedio.duracao_dias) campos.duracao_dias = emDias

    // A hora só vai se ela mexeu nela: mandar a mesma refaria o esquema à toa.
    // Vai como está no campo — "2026-09-06T21:00", sem fuso —, que é a hora da
    // parede da casa. Quem sabe em que fuso isso cai é o servidor, que conhece
    // a casa; o aparelho pode estar em qualquer lugar.
    if (proxima && proxima !== paraCampo(remedio.proxima_dose_em))
      campos.proxima_dose = proxima

    if (Object.keys(campos).length === 0) {
      aoFechar()
      return
    }

    setSalvando(true)
    setErro(null)
    try {
      await aoSalvar(campos)
    } catch (e) {
      setErro(fraseDoErro(e, 'Não consegui salvar agora.'))
      setSalvando(false)
    }
  }

  return (
    <form className="remedio__corrigindo" onSubmit={salvar}>
      <p className="remedio__aviso">
        Isso corrige o que eu entendi. Se o médico mudou a receita, me manda a foto nova.
      </p>

      <label className="remedio__campo">
        <span>Remédio</span>
        <input value={nome} onChange={(e) => setNome(e.target.value)} maxLength={120} />
      </label>

      <label className="remedio__campo">
        <span>Concentração</span>
        <input
          value={concentracao}
          onChange={(e) => setConcentracao(e.target.value)}
          placeholder="250 mg/5 ml"
          maxLength={80}
        />
      </label>

      <label className="remedio__campo">
        <span>Quanto dar</span>
        <input
          value={dose}
          onChange={(e) => setDose(e.target.value)}
          placeholder="5 ml"
          maxLength={60}
        />
      </label>

      <label className="remedio__campo">
        <span>Como dar</span>
        <input
          value={via}
          onChange={(e) => setVia(e.target.value)}
          placeholder="por boca"
          maxLength={40}
        />
      </label>

      <label className="remedio__campo">
        <span>De quantas em quantas horas</span>
        <input
          type="number"
          inputMode="numeric"
          min={1}
          max={168}
          value={horas}
          onChange={(e) => setHoras(e.target.value)}
        />
      </label>

      <label className="remedio__campo">
        <span>Por quantos dias</span>
        <input
          type="number"
          inputMode="numeric"
          min={1}
          max={365}
          value={dias}
          onChange={(e) => setDias(e.target.value)}
          placeholder="deixe vazio se é todo dia, sem prazo"
        />
      </label>

      {remedio.proxima_dose_em && (
        <label className="remedio__campo">
          <span>A próxima é</span>
          <input
            type="datetime-local"
            value={proxima}
            onChange={(e) => setProxima(e.target.value)}
          />
        </label>
      )}

      <label className="remedio__campo">
        <span>Alguma observação</span>
        <textarea
          value={observacoes}
          onChange={(e) => setObservacoes(e.target.value)}
          rows={2}
          maxLength={300}
        />
      </label>

      {erro && (
        <p className="remedio__erro" role="alert">
          {erro}
        </p>
      )}

      {encerrando ? (
        <>
          <p className="remedio__aviso">
            Paro de te lembrar desse. O que você já deu fica registrado.
          </p>
          <div className="remedio__acoes">
            <button
              type="button"
              className="conf-pessoa__tirar"
              onClick={() => void aoEncerrar()}
            >
              Pode encerrar
            </button>
            <button
              type="button"
              className="remedio__deixar"
              onClick={() => setEncerrando(false)}
            >
              Continua valendo
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="remedio__acoes">
            <button type="submit" className="botao-primario" disabled={salvando}>
              {salvando ? 'Salvando…' : 'Salvar'}
            </button>
            <button type="button" className="remedio__deixar" onClick={aoFechar}>
              Deixa como está
            </button>
          </div>
          <button
            type="button"
            className="conf-pessoa__tirar-link"
            onClick={() => setEncerrando(true)}
          >
            Esse tratamento acabou
          </button>
        </>
      )}
    </form>
  )
}

/**
 * A mãe escreve o esquema, e confirma numa frase antes de virar lembrete.
 *
 * A foto da receita é o caminho e continua sendo. Isto é para quando não há
 * papel: o pediatra falou no telefone, a receita ficou na farmácia, a foto
 * saiu ilegível. Sem esta porta, o remédio da criança fica de fora do produto
 * justamente nos dias ruins.
 *
 * **Nenhum campo vem preenchido, e é a regra 1.** "Remédio nunca é inferido":
 * um valor sugerido aqui é a Ruth chutando dose de criança. Ela escreve tudo,
 * lê a frase que repete tudo, e só então existe esquema — a mesma frase e o
 * mesmo "sim" que a conversa pediria, porque a regra não muda de exigência
 * quando o caminho muda.
 */
function Montar({
  aoMontar,
  aoFechar,
}: {
  aoMontar: (novo: RemedioNovo) => Promise<void>
  aoFechar: () => void
}) {
  const [nome, setNome] = useState('')
  const [paraQuem, setParaQuem] = useState('')
  const [concentracao, setConcentracao] = useState('')
  const [dose, setDose] = useState('')
  const [via, setVia] = useState('')
  const [horas, setHoras] = useState('')
  const [dias, setDias] = useState('')
  const [primeira, setPrimeira] = useState('')
  const [conferindo, setConferindo] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const completo =
    nome.trim() && paraQuem.trim() && dose.trim() && Number(horas) >= 1 && primeira

  const [frase, setFrase] = useState('')

  const oQueVaiSerGravado = (): RemedioNovo => ({
    nome: nome.trim(),
    para_quem: paraQuem.trim(),
    dose: dose.trim(),
    intervalo_horas: Number(horas),
    concentracao: concentracao.trim() || null,
    via: via.trim() || null,
    duracao_dias: dias.trim() ? Number(dias) : null,
    primeira_dose: primeira,
    confirmo: true,
  })

  /** Pede ao servidor a frase do que ela escreveu, e só então mostra. */
  const conferir = async () => {
    setErro(null)
    try {
      const { frase: dita } = await api.conferirRemedio(oQueVaiSerGravado())
      setFrase(dita)
      setConferindo(true)
    } catch (e) {
      setErro(fraseDoErro(e, 'Não consegui montar a frase agora.'))
    }
  }

  const guardar = async () => {
    if (salvando) return
    setSalvando(true)
    setErro(null)
    try {
      await aoMontar(oQueVaiSerGravado())
    } catch (e) {
      setErro(fraseDoErro(e, 'Não consegui montar agora.'))
      setSalvando(false)
      setConferindo(false)
    }
  }

  if (conferindo) {
    return (
      <div className="cartao remedio__corrigindo">
        <p className="remedio__aviso">Confere pra mim, palavra por palavra:</p>
        <p className="remedio__confere">{frase}</p>
        {erro && (
          <p className="remedio__erro" role="alert">
            {erro}
          </p>
        )}
        <div className="remedio__acoes">
          <button
            type="button"
            className="botao-primario"
            disabled={salvando}
            onClick={() => void guardar()}
          >
            {salvando ? 'Guardando…' : 'Sim, é isso'}
          </button>
          <button
            type="button"
            className="remedio__deixar"
            onClick={() => setConferindo(false)}
          >
            Deixa eu arrumar
          </button>
        </div>
      </div>
    )
  }

  return (
    <form
      className="cartao remedio__corrigindo"
      onSubmit={(e) => {
        e.preventDefault()
        if (completo) void conferir()
      }}
    >
      <p className="remedio__aviso">
        Escreve o que está na receita. Eu não completo nada por você — remédio de
        criança não é lugar de eu adivinhar.
      </p>

      <label className="remedio__campo">
        <span>Remédio</span>
        <input value={nome} onChange={(e) => setNome(e.target.value)} maxLength={120} />
      </label>

      <label className="remedio__campo">
        <span>Pra quem</span>
        <input
          value={paraQuem}
          onChange={(e) => setParaQuem(e.target.value)}
          placeholder="o nome, como está aqui em casa"
          maxLength={120}
        />
      </label>

      <label className="remedio__campo">
        <span>Concentração</span>
        <input
          value={concentracao}
          onChange={(e) => setConcentracao(e.target.value)}
          placeholder="250 mg/5 ml"
          maxLength={80}
        />
      </label>

      <label className="remedio__campo">
        <span>Quanto dar</span>
        <input
          value={dose}
          onChange={(e) => setDose(e.target.value)}
          placeholder="5 ml"
          maxLength={60}
        />
      </label>

      <label className="remedio__campo">
        <span>Como dar</span>
        <input
          value={via}
          onChange={(e) => setVia(e.target.value)}
          placeholder="por boca"
          maxLength={40}
        />
      </label>

      <label className="remedio__campo">
        <span>De quantas em quantas horas</span>
        <input
          type="number"
          inputMode="numeric"
          min={1}
          max={168}
          value={horas}
          onChange={(e) => setHoras(e.target.value)}
        />
      </label>

      <label className="remedio__campo">
        <span>Por quantos dias</span>
        <input
          type="number"
          inputMode="numeric"
          min={1}
          max={365}
          value={dias}
          onChange={(e) => setDias(e.target.value)}
          placeholder="vazio se é todo dia, sem prazo"
        />
      </label>

      <label className="remedio__campo">
        <span>A primeira dose é</span>
        <input
          type="datetime-local"
          value={primeira}
          onChange={(e) => setPrimeira(e.target.value)}
        />
      </label>

      <div className="remedio__acoes">
        <button type="submit" className="botao-primario" disabled={!completo}>
          Conferir
        </button>
        <button type="button" className="remedio__deixar" onClick={aoFechar}>
          Deixa pra lá
        </button>
      </div>
    </form>
  )
}

/**
 * O que o campo de data e hora espera: "2026-09-06T21:00".
 *
 * O servidor já manda a hora da parede da casa, sem fuso, então aqui é corte de
 * texto e nada mais. Passar por `Date` reescreveria a hora no fuso do aparelho
 * — que é o erro que a mãe não veria: o campo abriria com 18h onde a dose é
 * 21h, e ela salvaria achando que não tinha mexido em nada.
 */
function paraCampo(iso: string | null): string {
  return iso ? iso.slice(0, 16) : ''
}

/** "dose 9 de 21" — a única barra de progresso do produto, e ela conta cuidado. */
function Barra({ atual, total }: { atual: number; total: number }) {
  const parte = total > 0 ? Math.min(atual / total, 1) : 0

  return (
    <div className="remedio__barra">
      <div
        className="remedio__trilho"
        role="progressbar"
        aria-valuenow={atual}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-label={`dose ${atual} de ${total}`}
      >
        <div className="remedio__cheio" style={{ width: `${parte * 100}%` }} />
      </div>
      <span className="remedio__contagem">
        dose {atual} de {total}
      </span>
    </div>
  )
}

function Bolinha({ dose }: { dose: DoseDeHoje }) {
  return (
    <div className="remedio__dose">
      <span className={`remedio__bolinha remedio__bolinha--${dose.status}`}>
        {dose.status === 'dada' && <Icone nome="check" tamanho={12} />}
      </span>
      <span className="remedio__hora">{hhmm(dose.hora)}</span>
    </div>
  )
}

/** "5 ml de 8 em 8 h · termina 11/9" — só o que veio da receita, nada além. */
function descrever(remedio: Remedio): string {
  const partes = [[remedio.dose, remedio.intervalo_texto].filter(Boolean).join(' ')]

  if (remedio.continuo) {
    // Sem intervalo escrito, a hora da dose é o que diz quando é.
    const hoje = remedio.doses_de_hoje[0]
    if (hoje && !remedio.intervalo_texto) partes.push(hhmm(hoje.hora))
    partes.push('contínuo')
  } else if (remedio.termina_em) {
    partes.push(`termina ${diaEMes(remedio.termina_em)}`)
  }

  return partes.filter(Boolean).join(' · ')
}

/**
 * A linha da receita, e o papel guardado.
 *
 * A Ruth prometeu a ela: "guardei a foto da receita junto, se precisar mostrar
 * na farmácia". Este é o lugar onde a promessa vira um toque.
 *
 * A imagem não vai num `<img src>` direto porque a rota exige identidade, e
 * `src` não carrega cabeçalho. Ela vem por `fetch` e vira um endereço de blob,
 * solto quando a mãe fecha — senão a receita fica na memória da aba até ela
 * sair do app.
 */
function DaReceita({ receita }: { receita: Receita }) {
  const [aberta, setAberta] = useState<string | null>(null)
  const [buscando, setBuscando] = useState(false)

  useEffect(() => () => { if (aberta) URL.revokeObjectURL(aberta) }, [aberta])

  const abrir = async () => {
    setBuscando(true)
    try {
      setAberta(await fotoDaReceita(receita.id))
    } catch {
      setAberta(null)
    } finally {
      setBuscando(false)
    }
  }

  return (
    <div className="remedio__receita">
      <Icone nome="camera" tamanho={14} />
      Receita guardada{detalhesDaReceita(receita)}
      {receita.tem_foto && (
        <button type="button" className="remedio__ver-receita" onClick={abrir} disabled={buscando}>
          {buscando ? 'abrindo' : 'ver o papel'}
        </button>
      )}
      {aberta && (
        <div className="receita-aberta" onClick={() => setAberta(null)} role="presentation">
          <img src={aberta} alt="A receita que você me mandou" />
          <button type="button" className="receita-aberta__fechar">
            Fechar
          </button>
        </div>
      )}
    </div>
  )
}

/** O médico não está no modelo, então a frase encolhe em vez de inventar um nome. */
function detalhesDaReceita(receita: { medico: string | null; data: string | null }): string {
  const detalhes = [receita.medico, receita.data ? diaEMes(receita.data) : null].filter(Boolean)
  return detalhes.length ? ` · ${detalhes.join(', ')}` : ''
}

/**
 * "2026-09-11" vira "11/9". Sai do texto do ISO, não de `new Date`: a data
 * chega no fuso da casa e passar por `Date` a moveria para o do aparelho.
 */
function diaEMes(iso: string): string {
  const [, mes, dia] = iso.split('-').map(Number)
  return `${dia}/${mes}`
}
